import { Badge, type BadgeVariant } from "../../ds";
import type { ModemStatus } from "../../lib/api";
import {
  rateScore,
  rateSignal,
  SIGNAL_LABEL,
  SIGNAL_METRICS,
  SIGNAL_UNIT,
  signalPct,
  signalScale,
  signalScore,
  signalZones,
  type SignalMetric,
  type SignalQuality,
} from "../../lib/format";

// Funkwerte des Modems als Skala statt als nackte Zahl: -107 dBm sagt dem
// Bediener nichts, „im roten Drittel" schon. Rein rechnerisch aus dem, was der
// Router ohnehin im `modem_status` schickt — kein zusätzliches Byte über LTE.

const QUALITY_VARIANT: Record<SignalQuality, BadgeVariant> = {
  good: "success",
  mid: "warn",
  bad: "danger",
};

/** Farbe der Zeigerlinie bzw. des Bogens — Tailwind-Text-Token, via currentColor. */
const QUALITY_TEXT: Record<SignalQuality, string> = {
  good: "text-success",
  mid: "text-warning",
  bad: "text-danger",
};

const ZONE_BG: [string, string, string] = ["bg-danger/25", "bg-warning/25", "bg-success/25"];

/** Halbkreis-Bogen: Radius 50 um (60|60), von links nach rechts. */
const ARC = "M 10 60 A 50 50 0 0 1 110 60";
const ARC_LEN = Math.PI * 50;

function QualityGauge({ pct }: { pct: number | null }) {
  const quality = pct === null ? null : rateScore(pct);
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-line bg-sunken p-4">
      <svg viewBox="0 0 120 72" className="w-full max-w-[180px]" role="presentation">
        <path
          d={ARC}
          fill="none"
          strokeWidth={9}
          strokeLinecap="round"
          className="stroke-current text-content-muted opacity-30"
        />
        {pct !== null && quality && (
          <path
            d={ARC}
            fill="none"
            strokeWidth={9}
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * ARC_LEN} ${ARC_LEN}`}
            className={`stroke-current ${QUALITY_TEXT[quality]}`}
          />
        )}
        <text
          x={60}
          y={54}
          textAnchor="middle"
          className="fill-current text-[22px] font-semibold text-content"
        >
          {pct === null ? "—" : `${pct}%`}
        </text>
      </svg>
      <div className="text-xs text-content-3">Signalqualität</div>
      {quality && <Badge variant={QUALITY_VARIANT[quality]}>{SIGNAL_LABEL[quality]}</Badge>}
    </div>
  );
}

/** Ein Funkwert: Zahl, Einordnung und die Zone, in der er liegt. */
function MetricTile({ metric, value }: { metric: SignalMetric; value: number | undefined }) {
  const quality = rateSignal(metric, value);
  const pct = signalPct(metric, value);
  const [min, max] = signalScale(metric);
  const zones = signalZones(metric);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-content-3">{metric}</span>
        {quality && <Badge variant={QUALITY_VARIANT[quality]}>{SIGNAL_LABEL[quality]}</Badge>}
      </div>
      <div className="text-xl font-semibold tabular-nums text-content">
        {value ?? "—"}
        {value !== undefined && (
          <span className="ml-1 text-xs font-normal text-content-3">{SIGNAL_UNIT[metric]}</span>
        )}
      </div>
      <div className="relative h-2 overflow-hidden rounded-full">
        <div className="flex h-full w-full">
          {zones.map((width, i) => (
            <div key={i} className={ZONE_BG[i]} style={{ width: `${width}%` }} />
          ))}
        </div>
        {pct !== null && quality && (
          // Zeiger sitzt auf dem Wert; -50% zieht ihn über seinen eigenen Punkt.
          <div
            className={`absolute top-0 h-full w-1 -translate-x-1/2 rounded-full bg-current ${QUALITY_TEXT[quality]}`}
            style={{ left: `${pct}%` }}
          />
        )}
      </div>
      <div className="flex justify-between text-[10px] tabular-nums text-content-muted">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

export function SignalPanel({ modem }: { modem: ModemStatus }) {
  return (
    <div className="grid gap-3 lg:grid-cols-[200px_minmax(0,1fr)]">
      <QualityGauge pct={signalScore(modem)} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {SIGNAL_METRICS.map((metric) => (
          <MetricTile key={metric} metric={metric} value={modem[metric]} />
        ))}
      </div>
    </div>
  );
}
